/**
 * Landing Layout Showcase
 * A page to preview and compare all landing page layout variants
 */
import { useMemo, useState } from "react";
import {
  FieldGuideLanding,
  LAYOUT_OPTIONS,
  type LayoutId,
  LodgeBulletinLanding,
  NatureMinimalLanding,
  SummitLanding,
  TopographicLanding,
} from "./landing-layouts";
import type { PublishedLandingPage, PlaceGeoJSON } from "../lib/api";

// Mock data for preview
const mockMapData: PublishedLandingPage = {
  slug: "demo-adventure",
  name: "Mountain Valley Adventures",
  description:
    "Discover the hidden gems of our mountain valley. From cozy cafes to breathtaking trails, this guide has everything you need for an unforgettable adventure.",
  config: {},
  logo: null,
  publishedAt: new Date(),
  updatedAt: new Date(),
  curatedBy: { displayName: "Demo Curator" },
};

const mockPlaces: PlaceGeoJSON = {
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      id: "place-1",
      geometry: { type: "Point", coordinates: [-122.4194, 37.7749] },
      properties: {
        id: "place-1",
        status: "published",
        permissions: { canEdit: false, canDelete: false, canPublish: false, canTransfer: false },
        name: "Summit Coffee Roasters",
        type: "cafe",
        description: "Try their signature mountain blend. Best enjoyed on the sunny patio with views of the peaks.",
        links: [],
        images: [],
        email: null,
        phone: null,
        metadata: null,
      },
    },
    {
      type: "Feature",
      id: "place-2",
      geometry: { type: "Point", coordinates: [-122.4094, 37.7849] },
      properties: {
        id: "place-2",
        status: "published",
        permissions: { canEdit: false, canDelete: false, canPublish: false, canTransfer: false },
        name: "The Trailhead Bistro",
        type: "restaurant",
        description:
          "Local favorite for farm-to-table dining. The elk burger is legendary. Reserve ahead for weekend dinners.",
        links: [],
        images: [],
        email: null,
        phone: null,
        metadata: null,
      },
    },
    {
      type: "Feature",
      id: "place-3",
      geometry: { type: "Point", coordinates: [-122.4294, 37.7649] },
      properties: {
        id: "place-3",
        status: "published",
        permissions: { canEdit: false, canDelete: false, canPublish: false, canTransfer: false },
        name: "Eagle Ridge Trail",
        type: "hiking",
        description:
          "Start early to catch sunrise from the summit. The 6km loop takes about 3 hours at a relaxed pace.",
        links: [],
        images: [],
        email: null,
        phone: null,
        metadata: null,
      },
    },
    {
      type: "Feature",
      id: "place-4",
      geometry: { type: "Point", coordinates: [-122.4394, 37.7549] },
      properties: {
        id: "place-4",
        status: "published",
        permissions: { canEdit: false, canDelete: false, canPublish: false, canTransfer: false },
        name: "Valley View Lodge",
        type: "accommodation",
        description: "Room 12 has the best mountain views. Ask for the homemade granola at breakfast.",
        links: [],
        images: [],
        email: null,
        phone: null,
        metadata: null,
      },
    },
    {
      type: "Feature",
      id: "place-5",
      geometry: { type: "Point", coordinates: [-122.4144, 37.7699] },
      properties: {
        id: "place-5",
        status: "published",
        permissions: { canEdit: false, canDelete: false, canPublish: false, canTransfer: false },
        name: "Mountain Market",
        type: "grocery-store",
        description: "Best selection of local cheeses and craft beers. Perfect for picnic supplies.",
        links: [],
        images: [],
        email: null,
        phone: null,
        metadata: null,
      },
    },
    {
      type: "Feature",
      id: "place-6",
      geometry: { type: "Point", coordinates: [-122.4244, 37.7799] },
      properties: {
        id: "place-6",
        status: "published",
        permissions: { canEdit: false, canDelete: false, canPublish: false, canTransfer: false },
        name: "Wildflower Meadow Loop",
        type: "hiking",
        description: "Best in late spring when wildflowers bloom. Easy 2km family-friendly trail.",
        links: [],
        images: [],
        email: null,
        phone: null,
        metadata: null,
      },
    },
    {
      type: "Feature",
      id: "place-7",
      geometry: { type: "Point", coordinates: [-122.4344, 37.7899] },
      properties: {
        id: "place-7",
        status: "published",
        permissions: { canEdit: false, canDelete: false, canPublish: false, canTransfer: false },
        name: "Mountainside Cafe",
        type: "cafe",
        description: "Their lavender latte is divine. Great wifi for remote workers.",
        links: [],
        images: [],
        email: null,
        phone: null,
        metadata: null,
      },
    },
  ],
};

const layoutComponents: Record<
  LayoutId,
  React.ComponentType<{ mapData: PublishedLandingPage; places: PlaceGeoJSON }>
> = {
  "field-guide": FieldGuideLanding,
  topographic: TopographicLanding,
  "lodge-bulletin": LodgeBulletinLanding,
  "nature-minimal": NatureMinimalLanding,
  summit: SummitLanding,
};

export default function LandingLayoutShowcase() {
  const [activeLayout, setActiveLayout] = useState<LayoutId>("field-guide");

  const ActiveComponent = useMemo(() => layoutComponents[activeLayout], [activeLayout]);

  return (
    <div className="min-h-screen bg-background">
      {/* Floating layout selector */}
      <div
        className="fixed top-4 left-1/2 -translate-x-1/2 z-[1000] p-2 rounded-2xl backdrop-blur-md"
        style={{
          background: "rgba(255,255,255,0.9)",
          boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
          border: "1px solid rgba(0,0,0,0.1)",
        }}
      >
        <div className="flex items-center gap-1">
          <span className="px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Layout:</span>
          {LAYOUT_OPTIONS.map((layout) => (
            <button
              key={layout.id}
              onClick={() => setActiveLayout(layout.id)}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                activeLayout === layout.id
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-foreground hover:bg-muted"
              }`}
            >
              {layout.name}
            </button>
          ))}
        </div>
      </div>

      {/* Active layout preview */}
      <ActiveComponent key={activeLayout} mapData={mockMapData} places={mockPlaces} />
    </div>
  );
}
